import { Badge } from '../ui/badge';
import { ShieldCheck, ShieldAlert } from 'lucide-react';

/**
 * Compact "Terms & Conditions on file" badge for the admin customer/parent
 * profile header. Renders:
 *   • Green pill with the first-acceptance date when consent is recorded.
 *   • Slate "no consent on file" pill otherwise.
 *
 * Props mirror the keys the backend ships from
 * GET /api/admin/customers/{id} and GET /api/admin/parents/{id}.
 */
const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return null;
  }
};

export const TermsBadge = ({
  termsAcceptedAt,
  termsLastAcceptedAt,
  termsSource,
  className = '',
  testId = 'terms-badge',
}) => {
  const acceptedAt = termsAcceptedAt || termsLastAcceptedAt;
  const acceptedDate = fmtDate(acceptedAt);
  const lastDate = fmtDate(termsLastAcceptedAt);
  const sourceLabel = termsSource === 'lane_booking_walkin'
    ? 'walk-in booking'
    : termsSource === 'lane_booking_self'
      ? 'lane booking'
      : 'reservation';

  if (!acceptedDate) {
    return (
      <Badge
        variant="outline"
        className={`bg-slate-50 text-slate-600 border-slate-200 inline-flex items-center gap-1 ${className}`}
        data-testid={`${testId}-missing`}
        title="No Terms & Conditions consent has been recorded for this customer yet."
      >
        <ShieldAlert className="h-3 w-3" />
        T&amp;C not on file
      </Badge>
    );
  }

  const tooltip = [
    `First accepted: ${acceptedDate}`,
    lastDate && lastDate !== acceptedDate ? `Last accepted: ${lastDate}` : null,
    `Via: ${sourceLabel}`,
  ].filter(Boolean).join(' · ');

  return (
    <Badge
      variant="outline"
      className={`bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1 ${className}`}
      data-testid={testId}
      title={tooltip}
    >
      <ShieldCheck className="h-3 w-3" />
      T&amp;C accepted {acceptedDate}
    </Badge>
  );
};

export default TermsBadge;
